
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useContactForm } from '@/hooks/useContactForm';
import FormFieldWrapper from './contact/FormFieldWrapper';
import FormSuccessMessage from './contact/FormSuccessMessage';
import SubmitButton from './contact/SubmitButton';
import ResponseTimeIndicator from './contact/ResponseTimeIndicator';
import FileUploadField from './contact/FileUploadField';
import ContactInfoCards from './contact/ContactInfoCards';
import ContactFAQ from './contact/ContactFAQ';

const ContactForm = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  const { 
    form, 
    isSubmitting, 
    isSuccess, 
    handleSubmit, 
    attachment, 
    setAttachment
  } = useContactForm(
    t.formSubmitSuccess,
    t.formSubmitMessage
  );

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" id="contact">
      {/* Neural network background animation */}
      <div className="absolute inset-0 pointer-events-none neural-pattern opacity-20"></div>
      
      {/* Light streaks */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute h-[300px] w-[2px] bg-gradient-to-b from-transparent via-purple-500/20 to-transparent top-0 left-[30%] animate-[float_15s_ease-in-out_infinite]"></div>
        <div className="absolute h-[200px] w-[2px] bg-gradient-to-b from-transparent via-purple-500/10 to-transparent top-[20%] left-[70%] animate-[float_20s_ease-in-out_infinite_reverse]"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white font-future tracking-wider">
              {t.contactUs}
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-6">
              {t.contactSubtitle}
            </p>
            <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-2 animate-pulse"></div>
          </div>
          
          {/* Contact Information Cards */}
          <ContactInfoCards />
          
          <div className="glass-card rounded-xl p-6 md:p-10 shadow-lg shadow-purple-500/10 transform transition-all duration-500 hover:shadow-purple-500/20 animate-scale-in">
            {isSuccess ? (
              <FormSuccessMessage 
                title={t.formSubmitSuccess} 
                message={t.formSubmitMessage} 
              />
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6" id="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormFieldWrapper
                      form={form}
                      name="name"
                      label={t.fullName}
                      placeholder={t.fullName}
                      required
                    >
                      <Input />
                    </FormFieldWrapper>
                    
                    <FormFieldWrapper
                      form={form}
                      name="email"
                      label={t.email}
                      placeholder="email@example.com"
                      required
                    >
                      <Input type="email" />
                    </FormFieldWrapper>
                    
                    <FormFieldWrapper
                      form={form}
                      name="company"
                      label={t.company}
                      placeholder={t.company}
                    >
                      <Input />
                    </FormFieldWrapper>
                    
                    <FormFieldWrapper
                      form={form}
                      name="phone"
                      label={t.phone}
                      placeholder="+49 123 456789"
                    >
                      <Input type="tel" />
                    </FormFieldWrapper>
                  </div>
                  
                  <FormFieldWrapper
                    form={form}
                    name="message"
                    label={t.projectDescription}
                    placeholder={t.projectDescription}
                    required
                  >
                    <Textarea className="min-h-[120px]" />
                  </FormFieldWrapper>
                  
                  {/* File Upload Field */}
                  <div className="space-y-2">
                    <label className="text-gray-200 block">
                      {currentLanguage === 'de' ? 'Datei anhängen (optional)' : 
                       currentLanguage === 'fr' ? 'Joindre un fichier (optionnel)' :
                       currentLanguage === 'it' ? 'Allega un file (opzionale)' :
                       'Attach a file (optional)'}
                    </label>
                    <FileUploadField
                      value={attachment}
                      onChange={setAttachment}
                      placeholder={
                        currentLanguage === 'de' ? 'Ziehen Sie eine Datei hierher oder klicken Sie zum Durchsuchen' :
                        currentLanguage === 'fr' ? 'Glissez un fichier ici ou cliquez pour parcourir' :
                        currentLanguage === 'it' ? 'Trascina un file qui o clicca per sfogliare' :
                        'Drag and drop a file here or click to browse'
                      }
                    />
                  </div>
                  
                  <div className="text-center pt-4">
                    <div className="flex flex-col items-center">
                      <div className="transform transition-all duration-300 hover:scale-105">
                        <SubmitButton 
                          isSubmitting={isSubmitting}
                          sendText={t.send}
                          sendingText={t.sending}
                        />
                      </div>
                      
                      <ResponseTimeIndicator text={t.responseTime} />
                    </div>
                  </div>
                </form>
              </Form>
            )}
          </div>
          
          {/* FAQ Section */}
          <ContactFAQ />
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
